// Badge for a creator whose platform profile could not be fetched, both
// platforms. PRIVATE and NOT FOUND show to admin and brand viewers; COULDN'T
// CHECK (unavailable) shows to admins only. Mirrors the BE lookup_status
// values (profilesync.Lookup*).

import type { CreatorLookupStatus } from '@discovr/types';
import type { CreatorsRow } from './types';

/** lookup_status lives only on Creators-tab rows, not rejected rows. */
export function rowLookupStatus(row: CreatorsRow): CreatorLookupStatus | undefined {
  if (!('lookup_status' in row)) return undefined;
  return row.lookup_status;
}

/** A row's latest fetch as the re-check spinner tracks it: the outcome and
 *  when it settled. Either changing means a newer fetch has landed. */
export type CreatorLookup = Readonly<{
  status: CreatorLookupStatus | undefined;
  checkedAt: string | undefined;
}>;

export function rowLookup(row: CreatorsRow): CreatorLookup {
  const checkedAt = 'lookup_checked_at' in row ? row.lookup_checked_at : undefined;
  return { status: rowLookupStatus(row), checkedAt };
}

export const LOOKUP_LABEL_PRIVATE = 'PRIVATE';
export const LOOKUP_LABEL_NOT_FOUND = 'NOT FOUND';
export const LOOKUP_LABEL_UNAVAILABLE = "COULDN'T CHECK";

/** private: the account exists but hides its data — the admin can contact the
 *  creator. missing: no account resolves for the handle. unchecked: the
 *  provider couldn't answer, so the handle is neither confirmed nor ruled out. */
export type CreatorLookupTone = 'private' | 'missing' | 'unchecked';

export type CreatorLookupPill = {
  label: string;
  tone: CreatorLookupTone;
};

export type CreatorLookupBadge = {
  pill: CreatorLookupPill | null;
  showRecheck: boolean;
};

const PRIVATE_PILL: CreatorLookupPill = { label: LOOKUP_LABEL_PRIVATE, tone: 'private' };
const NOT_FOUND_PILL: CreatorLookupPill = { label: LOOKUP_LABEL_NOT_FOUND, tone: 'missing' };
const UNAVAILABLE_PILL: CreatorLookupPill = { label: LOOKUP_LABEL_UNAVAILABLE, tone: 'unchecked' };

const NO_BADGE: CreatorLookupBadge = { pill: null, showRecheck: false };

const UNAVAILABLE_ADMIN_BADGE: CreatorLookupBadge = { pill: UNAVAILABLE_PILL, showRecheck: true };

const BADGE_BY_STATUS: Record<CreatorLookupStatus, CreatorLookupBadge> = {
  active: NO_BADGE,
  private: { pill: PRIVATE_PILL, showRecheck: true },
  not_found: { pill: NOT_FOUND_PILL, showRecheck: true },
  mismatch: { pill: NOT_FOUND_PILL, showRecheck: false },
  failed: { pill: null, showRecheck: true },
  unavailable: NO_BADGE,
  manual: NO_BADGE,
};

/** Maps a row's lookup_status to its badge. Still-fetching (absent), active,
 *  and any value the BE adds before the FE knows it render nothing. `unavailable`
 *  (the lookup couldn't be checked) is an admin-only signal: brand viewers get
 *  no pill, so a provider hiccup never reads as a verdict on their creator. */
export function resolveCreatorLookupBadge(
  status: CreatorLookupStatus | undefined,
  { isAdmin = false }: { isAdmin?: boolean } = {},
): CreatorLookupBadge {
  if (!status) return NO_BADGE;
  if (status === 'unavailable') return isAdmin ? UNAVAILABLE_ADMIN_BADGE : NO_BADGE;
  return BADGE_BY_STATUS[status] ?? NO_BADGE;
}

export const PROFILE_RECHECK_LABEL = 'Re-check profile';

/** Names the row in the ⟳ button's accessible label so a buttons list reads
 *  "Re-check profile for @handle", not twenty identical entries. */
export function profileRecheckLabel(handle: string | undefined): string {
  const trimmed = handle?.trim();
  if (!trimmed) return PROFILE_RECHECK_LABEL;
  return `${PROFILE_RECHECK_LABEL} for @${trimmed}`;
}

export const METRICS_RECHECK_LABEL = 'Re-fetch metrics';

export function metricsRecheckLabel(handle: string | undefined): string {
  const trimmed = handle?.trim();
  return trimmed ? `${METRICS_RECHECK_LABEL} for @${trimmed}` : METRICS_RECHECK_LABEL;
}

/** Verdicts that leave a pending creator offerable to the brand: exactly the
 *  BE's allow-list (roster/domain/visibility.go offerableProfileVerdicts).
 *  Anything else, including failed and a value the FE doesn't know yet, fails
 *  closed, as the BE refuses it. */
const OFFERABLE_VERDICTS = new Set<string>(['active', 'manual']);

/** Whether the row's profile has been fetched — the same three fields the
 *  BE's HasFetchedProfileData reads. */
function hasFetchedProfileData(row: CreatorsRow): boolean {
  const m = row.snapshot_metrics;
  return (m?.followers ?? 0) > 0 || Boolean(row.platform?.profile_picture_url) || Boolean(row.platform?.name);
}

export type ProfileOfferability = Readonly<{ offerable: true } | { offerable: false; reason: string }>;

export const UNFETCHED_REASON = "This creator's profile hasn't been fetched yet";
export const UNOFFERABLE_REASON: Record<string, string> = {
  private: "This creator's account is private",
  not_found: 'No account was found for this handle',
  mismatch: 'This handle now resolves to a different account',
  failed: "This creator's profile couldn't be fetched",
  unavailable: "This creator's profile couldn't be checked",
};

/** The BE's HiddenFromBrand rule for a PENDING row, as the admin sees it:
 *  the server refuses to approve such a creator on the brand's behalf
 *  (409), so the control is disabled with the reason instead of failing on
 *  click. Non-pending rows are never gated — the decision is already made. */
export function profileOfferability(row: CreatorsRow): ProfileOfferability {
  if (row.status !== 'pending') return { offerable: true };
  return profileOfferabilityAtPending(row);
}

/** The BE's HiddenFromBrandAtPending: the same rule for a row about to move
 *  back to pending (roll back to Pending answers 409 PROFILE_UNOFFERABLE). */
export function profileOfferabilityAtPending(row: CreatorsRow): ProfileOfferability {
  const status = rowLookupStatus(row);
  if (status && !OFFERABLE_VERDICTS.has(status)) {
    return { offerable: false, reason: UNOFFERABLE_REASON[status] ?? "This creator's profile can't be offered to the brand" };
  }
  if (!hasFetchedProfileData(row)) return { offerable: false, reason: UNFETCHED_REASON };
  return { offerable: true };
}
